type ProjectTranslations = {
  [languageCode: string]: Record<string, any>;
};

type LanguageContent = {
  [languageCode: string]: string[];
};

type CrowdinManifest = {
  files: string[];
  languages: string[];
  timestamp: string;
  content: LanguageContent;
};

class TranslationService {
  private static instance: TranslationService;
  private translationsCache: Map<string, ProjectTranslations> = new Map();
  private manifestCache: Map<string, CrowdinManifest> = new Map();
  private static BASE_URL = 'https://i18n.oneweb.mercedes-benz.com';
  private static BASE_PHRASE_URL = `${this.BASE_URL}/projects/`;

  private constructor() {
    // Private constructor to prevent instantiation
  }

  public static getInstance(): TranslationService {
    if (!TranslationService.instance) {
      TranslationService.instance = new TranslationService();
    }
    return TranslationService.instance;
  }

  /**
   * Fetches the Crowdin manifest for a given distribution hash. Used to get the list of files, languages and content in a distribution.
   * @param distributionHash The distribution hash for Crowdin.
   * @returns The Crowdin manifest as a JSON object.
   */
  private async fetchCrowdinManifest(distributionHash: string): Promise<CrowdinManifest> {
    if (this.manifestCache.has(distributionHash)) {
      return this.manifestCache.get(distributionHash)!;
    }

    const manifestUrl = `${TranslationService.BASE_URL}/${distributionHash}/manifest.json`;

    try {
      const manifestResponse = await fetch(manifestUrl);
      if (!manifestResponse.ok) {
        throw new Error(`Error fetching Crowdin manifest: ${manifestResponse.statusText}`);
      }
      const responseManifest = await manifestResponse.json();
      const manifest: CrowdinManifest = {
        files: responseManifest.files,
        languages: responseManifest.languages,
        timestamp: responseManifest.timestamp,
        content: responseManifest.content,
      };
      this.manifestCache.set(distributionHash, manifest);
      return manifest;
    } catch (error) {
      console.error(error);
      throw error;
    }
  }

  /**
   * Fetches translations for a specific file from Crowdin.
   * @param file The file name to fetch the translation for.
   * @param distributionHash The distribution hash for Crowdin.
   * @returns The translation messages as a JSON object.
   */
  private async fetchCrowdinTranslation(file: string, distributionHash: string): Promise<Record<string, any>> {
    if (!this.translationsCache.has(distributionHash)) {
      this.translationsCache.set(distributionHash, {});
    }

    const projectTranslations = this.translationsCache.get(distributionHash)!;

    if (!projectTranslations[file]) {
      const { timestamp } = await this.fetchCrowdinManifest(distributionHash);
      const crowdinUrl = new URL(`${TranslationService.BASE_URL}/${distributionHash}${file}`);
      crowdinUrl.searchParams.append('timestamp', timestamp);

      const translation = await this.fetchTranslation(crowdinUrl);
      projectTranslations[file] = translation;
    }

    return projectTranslations[file];
  }

  /**
   * Fetches translations for a specific locale from Phrase.
   * @param locale The locale to fetch the translation for (e.g., 'en-US').
   * @param projectId The project ID in Phrase.
   * @param fileFormat The file format of the fetched translation (e.g., 'json').
   * @param queryStringOpt Optional. Query string parameters to be appended to the request URL.
   * @returns The translation messages as a JSON object.
   */
  private async fetchPhraseTranslation(
    locale: string,
    projectId: string,
    fileFormat: string,
    queryStringOpt?: Record<string, any>,
  ): Promise<Record<string, any>> {
    if (!this.translationsCache.has(projectId)) {
      this.translationsCache.set(projectId, {});
    }

    const projectTranslations = this.translationsCache.get(projectId)!;

    if (!projectTranslations[locale]) {
      const phraseUrl = new URL(`${TranslationService.BASE_PHRASE_URL}${projectId}/locales/${locale}/download`);
      phraseUrl.searchParams.append('file_format', fileFormat);

      if (queryStringOpt) {
        Object.keys(queryStringOpt).forEach((key) => {
          phraseUrl.searchParams.append(key, queryStringOpt[key]);
        });
      }

      const translation = await this.fetchTranslation(phraseUrl);
      projectTranslations[locale] = translation;
    }

    return projectTranslations[locale];
  }

  /**
   * Fetches translation messages from a given URL.
   * @param url The URL to fetch the translation messages from.
   * @returns The translation messages as a JSON object.
   */
  private async fetchTranslation(url: URL): Promise<Record<string, any>> {
    try {
      const translationRequestResponse = await fetch(url);
      if (!translationRequestResponse.ok) {
        throw new Error(`Error fetching translation: ${translationRequestResponse.statusText}`);
      }
      const translationMessages = await translationRequestResponse.json();
      return translationMessages;
    } catch (error) {
      console.error(`Error fetching translation: ${error}`);
      throw error;
    }
  }

  /**
   * Fetches translations for a specific file from Crowdin.
   * @param fileNames The file names to fetch translations for. Can be a single file name or an array of file names.
   * @param distributionHash The distribution hash for Crowdin.
   * @returns An object containing the translation messages by file name. If a single file name is provided, the return type will be Record<string, any>. If an array of file names is provided, the return type will be Record<string, Record<string, any>>.
   */
  public async getFileTranslations(
    fileNames: string | string[],
    distributionHash: string,
    options?: { merge?: boolean },
  ): Promise<Record<string, any>> {
    const { content } = await this.fetchCrowdinManifest(distributionHash);
    fileNames = Array.isArray(fileNames) ? fileNames : [fileNames];

    let translations: Record<string, any> = {};

    for (const file of fileNames) {
      if (content && !Object.values(content).some((fileList) => fileList.includes(file))) {
        throw new Error(`File ${file} not found in the manifest`);
      }
      const fileTranslations = await this.fetchCrowdinTranslation(file, distributionHash);
      if (options?.merge) {
        translations = { ...translations, ...fileTranslations };
      } else {
        translations[file] = fileTranslations;
      }
    }
    return translations;
  }

  /**
   * This method fetches translations for a specific locale from either Crowdin or Phrase. If only 2 arguments are provided, it fetches translations from Crowdin. If the third argument is provided (file_format), it fetches translations from Phrase.
   * This overload was created in order to facilitate the migration from Phrase to Crowdin.
   * @param locale the locale to fetch the translation for (e.g., 'en-US'). Crowdin locales: https://support.crowdin.com/developer/language-codes/
   * @param distributionOrProjectID  For Crowdin: the distribution hash. For Phrase: the project ID.
   * @param fileFormat  Optional. The file format of the fetched translation (required for Phrase).
   * @param queryStringOpt  Optional. Query string parameters to be appended to the request URL.
   * @returns An object containing the translation messages by locale.
   */
  public async getLocaleTranslations(
    locale: string,
    distributionOrProjectID: string,
    fileFormat?: string,
    queryStringOpt?: Record<string, any>,
  ): Promise<Record<string, any>> {
    if (fileFormat) {
      // For Phrase
      return this.fetchPhraseTranslation(locale, distributionOrProjectID, fileFormat, queryStringOpt);
    }

    // For Crowdin
    const { languages, content } = await this.fetchCrowdinManifest(distributionOrProjectID);

    // Find the best matching locale
    const matchingLocale = this.findMatchingLocale(locale, languages);

    // Use the matching locale to get translation files
    const translationFiles = content[matchingLocale];

    if (translationFiles.length === 0) {
      throw new Error(`No translation files found for locale ${matchingLocale}`);
    }

    let translations: Record<string, any> = {};
    await Promise.all(
      translationFiles.map(async (file) => {
        const fileTranslations = await this.fetchCrowdinTranslation(file, distributionOrProjectID);
        translations = { ...translations, ...fileTranslations };
      }),
    );

    return translations;
  }

  /**
   * Finds the best matching locale from available languages for a given locale.
   * @param givenLocale The locale to find a match for (e.g., 'de-DE').
   * @param availableLanguages Array of available language codes from the manifest ('ar', 'de', 'de-AT', 'pt-PT').
   * @returns The matching locale string or null if no match is found.
   * @throws {Error} If the given locale is not found in the available languages.
   */
  private findMatchingLocale(givenLocale: string, availableLanguages: string[]) {
    // de-DE
    if (availableLanguages.includes(givenLocale)) {
      return givenLocale;
    }

    // de
    const [language] = givenLocale.split('-');

    const matchingLocale = availableLanguages.find((crowdinLang) => crowdinLang === language);

    if (!matchingLocale) {
      throw new Error(`Locale ${givenLocale} not found in the manifest`);
    }

    return matchingLocale;
  }
}

const translationService = TranslationService.getInstance();

/**
 * This method fetches translations for a specific locale from either Crowdin or Phrase. If only 2 arguments are provided, it fetches translations from Crowdin. If the third argument is provided (file_format), it fetches translations from Phrase.
 * This overload was created in order to facilitate the migration from Phrase to Crowdin.
 * @param locale the locale to fetch the translation for (e.g., 'en-US'). Crowdin locales: https://support.crowdin.com/developer/language-codes/
 * @param distributionOrProjectID  For Crowdin: the distribution hash. For Phrase: the project ID.
 * @param fileFormat  Optional. The file format of the fetched translation (required for Phrase).
 * @param queryStringOpt  Optional. Query string parameters to be appended to the request URL.
 * @returns An object containing the translation messages by locale.
 */
export function getTranslation(
  locale: string,
  distributionOrProjectID: string,
  fileFormat?: string,
  queryStringOpt?: Record<string, any>,
) {
  return translationService.getLocaleTranslations(locale, distributionOrProjectID, fileFormat, queryStringOpt);
}

/**
 * @param fileNames  The file names to fetch translations for. Can be a single file name or an array of file names.
 * @param distributionHash  The distribution hash for Crowdin.
 * @returns An object containing the translation messages by file name. If a single file name is provided, the return type will be Record<string, any>. If an array of file names is provided, the return type will be Record<string, Record<string, any>>.
 */

export function getFileTranslation(
  fileNames: string | string[],
  distributionHash: string,
  options?: { merge?: boolean },
): Promise<Record<string, any>> {
  return translationService.getFileTranslations(fileNames, distributionHash, options);
}
